/**
 * Conversion between the hours a person works on a Kostenstelle and the percentage that is persisted on the
 * contract. The percentage is the stored format, but hours are what the users actually work with, so the
 * conversion has to be exact enough that entering a valid distribution never produces a validation error.
 */

/** the percentage is stored with two decimals, so the smallest unit we work in is 1/100 of a percent */
const UNITS_PER_PERCENT = 100;
const TOTAL_UNITS = 100 * UNITS_PER_PERCENT;

/**
 * Tolerance for the sum check. New data is exact by construction, this only keeps legacy contracts whose
 * percentages were rounded independently (99.99, 100.01) from blocking unrelated edits.
 */
export const PERCENT_SUM_TOLERANCE = 0.05;

/** below this the difference is not representable in the stored format anymore */
export const HOURS_EPSILON = 0.005;

const isUsableTotal = (fullHours: number) => Number.isFinite(fullHours) && fullHours > 0;

export const hoursToPercent = (hours: number, fullHours: number) =>
  isUsableTotal(fullHours) ? (hours / fullHours) * 100 : 0;

export const percentToHours = (percent: number, fullHours: number) =>
  isUsableTotal(fullHours) ? (percent / 100) * fullHours : 0;

export const roundHours = (hours: number) => Math.round(hours * 100) / 100;

export const roundPercent = (percent: number) => Math.round(percent * UNITS_PER_PERCENT) / UNITS_PER_PERCENT;

export const isPercentSumValid = (sum: number) => Math.abs(sum - 100) <= PERCENT_SUM_TOLERANCE;

/**
 * Explains an incomplete distribution in the unit the user works in. The percentage alone leaves them guessing
 * how many hours are actually missing.
 */
export const kostenstellenSumErrorMessage = (section: 'AG1' | 'AG2', sumPercent: number, fullHours?: number) => {
  const percentText = `${sumPercent.toFixed(2).replace('.', ',')} %`;

  if (!isUsableTotal(fullHours ?? 0))
    return `Die Stunden der ${section} sind nicht vollständig auf Kostenstellen verteilt: ${percentText} von 100 %.`;

  const total = fullHours as number;
  const assignedHours = roundHours(percentToHours(sumPercent, total));

  return (
    `Die Stunden der ${section} sind nicht vollständig auf Kostenstellen verteilt: ${percentText} von 100 % ` +
    `(${assignedHours.toFixed(2).replace('.', ',')} von ${total.toFixed(2).replace('.', ',')} Std.).`
  );
};

/** whether the entered hours cover exactly the hours of the Eingruppierung */
export const isFullyDistributed = (sumHours: number, fullHours: number) =>
  isUsableTotal(fullHours) && Math.abs(sumHours - fullHours) <= HOURS_EPSILON;

/**
 * Converts the hours per Kostenstelle into the percentages that get persisted.
 *
 * Rounding each percentage on its own loses up to half a unit per row, which is why three equal shares used to
 * end up as 33.33 three times and failed the "has to be 100" check. Here the shares are floored first and the
 * units that are left over are handed to the rows with the largest cut off remainder, so a fully distributed
 * set of hours always sums up to exactly 100.
 *
 * When the hours do not cover the Eingruppierung the percentages are left proportional to the total instead of
 * being inflated to 100, so an incomplete distribution stays visible as such.
 *
 * @param hours the hours entered per Kostenstelle
 * @param fullHours the weekly hours of the Eingruppierung
 * @param previousPercents the currently stored percentages, used to keep the result stable for untouched rows
 */
export const distributePercent = (
  hours: number[],
  fullHours: number,
  previousPercents: Array<number | null | undefined> = [],
): number[] => {
  if (!hours.length) return [];

  // without a total there is nothing to convert against, so the stored values stay untouched
  if (!isUsableTotal(fullHours)) return hours.map((_, index) => roundPercent(previousPercents[index] ?? 0));

  const sumHours = hours.reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0);
  const fullyDistributed = sumHours > 0 && isFullyDistributed(sumHours, fullHours);
  const denominator = fullyDistributed ? sumHours : fullHours;

  const exactUnits = hours.map(value => ((Number.isFinite(value) ? value : 0) / denominator) * TOTAL_UNITS);
  const flooredUnits = exactUnits.map(units => Math.floor(units));

  const targetUnits = fullyDistributed ? TOTAL_UNITS : Math.round(exactUnits.reduce((sum, units) => sum + units, 0));
  const distributedUnits = flooredUnits.reduce((sum, units) => sum + units, 0);
  let remainingUnits = targetUnits - distributedUnits;

  if (remainingUnits > 0) {
    const order = exactUnits
      .map((units, index) => ({
        index,
        remainder: units - flooredUnits[index]!,
        // a row that already carries the extra unit keeps it, so reopening a contract does not shuffle the
        // percentages around and produce changes the user never made
        keepsExtraUnit: Math.round((previousPercents[index] ?? 0) * UNITS_PER_PERCENT) > flooredUnits[index]! ? 1 : 0,
      }))
      .sort((a, b) => b.remainder - a.remainder || b.keepsExtraUnit - a.keepsExtraUnit || a.index - b.index);

    for (const entry of order) {
      if (remainingUnits <= 0) break;
      flooredUnits[entry.index]! += 1;
      remainingUnits--;
    }
  }

  return flooredUnits.map(units => units / UNITS_PER_PERCENT);
};
